import { create } from 'zustand';
import {
  defaultMemoryFilter,
  emptyTenantNav,
  nextTenantNav,
  prevTenantNav,
  type MemoryFilter,
  type TenantNav,
} from '../data/memoryState';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { isRangeKey, type RangeKey, type TenantKey } from '../data/types';

/* Cross-cutting UI state: what the operator is looking at, filtered by, and
 * has open. Kept apart from the data stores so a data refresh can never move
 * the operator's place, and so sign-out can clear data without disturbing a
 * range they chose.
 *
 * One field outlives the tab: the range. It is a preference, not a place — an
 * operator who reads the fleet at "7 days" wants "7 days" tomorrow — so it is
 * persisted to this browser's localStorage and survives reload and sign-out.
 * Nothing else here is persisted: an open modal, a drawer selection or a page
 * cursor restored from a previous session would put the operator somewhere
 * they did not navigate to, on data that may no longer exist. */

export type LedgerFilter =
  | 'all'
  | 'billable'
  | 'attributed'
  | 'conflicts'
  | 'errors';

export type EstateTab =
  | 'policy'
  | 'tenants'
  | 'deployments'
  | 'keys'
  | 'ledger'
  | 'settings';

export type DrawerTab = 'overview' | 'payload' | 'audit';

export type ModalKind =
  | { kind: 'addKey' }
  | { kind: 'editKey'; keyId: string }
  | { kind: 'deleteKey'; keyId: string }
  | { kind: 'newEstate' }
  | { kind: 'newTenant'; estateId: string }
  | { kind: 'tenantKey'; estateId: string; tenantId: string }
  | { kind: 'mintToken'; estateId?: string; tenantId?: string }
  | { kind: 'forgetMemory'; estateId: string; tenantId: string; id: string }
  | { kind: 'unavailable'; action: string; detail: string };

export interface UIState {
  /** The tenant the whole console is scoped into, or null for the operator view. */
  scope: TenantKey | null;
  scopePickerOpen: boolean;

  range: RangeKey;

  ledgerSearch: string;
  ledgerEstate: string;
  ledgerFilter: LedgerFilter;
  ledgerPageSize: number;
  /** The cursor stack — index 0 is the first page, so "previous" is a pop. */
  ledgerCursors: string[];
  ledgerPage: number;

  /** The memory browser (DR-75): its filter, the ledger-style cursor stack,
   *  the selected row and the debugger's last query. Not persisted. */
  memoryFilter: MemoryFilter;
  memoryPageSize: number;
  memoryCursors: string[];
  memoryPage: number;
  memorySelection: string | null;
  memoryRecallQuery: string;
  /** The estate overview (DR-86): the operator's estate choice ('' = none
   *  yet) and its cursor stack, tagged with the estate it pages. */
  memoryEstate: string;
  memoryTenantNav: TenantNav;
  memoryTenantPageSize: number;
  /** The event id of the row whose drawer is open. */
  ledgerSelection: string | null;
  drawerTab: DrawerTab;
  payloadRevealed: boolean;

  poolFilter: string;
  estateTab: EstateTab;
  tenantSearch: string;
  tenantMode: string;
  tenantPool: string;
  tenantSort: string;
  tenantSettingsOpen: boolean;

  modal: ModalKind | null;
  wizardStep: number;

  set<K extends keyof UIState>(key: K, value: UIState[K]): void;
  /** Change something the ledger query is keyed on. Always returns to page one:
   *  keeping the page index across a filter change silently skips rows, and the
   *  row an operator just searched for is exactly the one they would miss. */
  setLedgerQuery<K extends 'ledgerSearch' | 'ledgerEstate' | 'ledgerFilter' | 'ledgerPageSize'>(
    key: K,
    value: UIState[K],
  ): void;
  setScope(scope: TenantKey | null): void;
  openModal(modal: ModalKind): void;
  closeModal(): void;
  selectLedgerRow(eventId: string | null): void;
  clearLedgerFilters(): void;
  clearTenantFilters(): void;
  nextLedgerPage(cursor: string): void;
  prevLedgerPage(): void;

  /** Change something the memory query is keyed on. Always returns to page one. */
  setMemoryFilter(patch: Partial<MemoryFilter>): void;
  clearMemoryFilters(): void;
  selectMemory(id: string | null): void;
  nextMemoryPage(cursor: string): void;
  prevMemoryPage(): void;
  /** Open a tenant's memory browser at its first page: the browser's cursor
   *  stack is global, and a cursor from the last tenant browsed means nothing
   *  in the next. The filter is kept — it is the operator's question. */
  openMemoryBrowser(): void;
  setMemoryEstate(estate: string): void;
  nextMemoryTenantPage(estate: string, cursor: string): void;
  prevMemoryTenantPage(estate: string): void;
  reset(): void;
}

const initial = {
  scope: null as TenantKey | null,
  scopePickerOpen: false,
  range: '30 days' as RangeKey,
  ledgerSearch: '',
  ledgerEstate: 'all',
  ledgerFilter: 'all' as LedgerFilter,
  ledgerPageSize: 10,
  ledgerCursors: [] as string[],
  ledgerPage: 0,
  memoryFilter: defaultMemoryFilter,
  memoryPageSize: 25,
  memoryCursors: [] as string[],
  memoryPage: 0,
  memorySelection: null as string | null,
  memoryRecallQuery: '',
  memoryEstate: '',
  memoryTenantNav: emptyTenantNav,
  memoryTenantPageSize: 25,
  ledgerSelection: null as string | null,
  drawerTab: 'overview' as DrawerTab,
  payloadRevealed: false,
  poolFilter: 'all',
  estateTab: 'policy' as EstateTab,
  tenantSearch: '',
  tenantMode: 'all',
  tenantPool: 'all',
  tenantSort: 'cost',
  tenantSettingsOpen: false,
  modal: null as ModalKind | null,
  wizardStep: 0,
};

/** The localStorage key. Versioned separately by persist's `version`. */
export const UI_STORAGE_KEY = 'haus-console-ui';

/** Where the preference lives when there is no browser storage — tests, the
 *  build, a browser that refuses site data. An in-memory stand-in keeps the
 *  store's behaviour identical and persist quiet; handing persist no storage
 *  at all makes it warn on every write instead. */
const memoryStorage = (() => {
  const m = new Map<string, string>();
  const s: StateStorage = {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
  return s;
})();

/** A storage that cannot throw into the store. localStorage fails at the call,
 *  not only at the accessor — a full quota, a browser that revokes site data
 *  mid-session — and persist would propagate that out of whichever action
 *  wrote the preference: a range change, a modal, sign-out's reset. The
 *  preference is not worth failing any of those. A failed write is dropped
 *  (the setting is simply not remembered this time); a failed read is "nothing
 *  stored". */
export function guardedStorage(backing: StateStorage): StateStorage {
  return {
    getItem: (k) => {
      try {
        return backing.getItem(k);
      } catch {
        return null;
      }
    },
    setItem: (k, v) => {
      try {
        backing.setItem(k, v);
      } catch {
        // Dropped on purpose; see above.
      }
    },
    removeItem: (k) => {
      try {
        backing.removeItem(k);
      } catch {
        // Likewise.
      }
    },
  };
}

function browserStorage(): StateStorage {
  try {
    // The accessor itself throws where site data is blocked, not just reads.
    return typeof localStorage === 'undefined' ? memoryStorage : guardedStorage(localStorage);
  } catch {
    return memoryStorage;
  }
}

/** What survives a reload: the range, and only if it is one this build knows.
 *  A value from an older build, a hand-edited entry, or a corrupt one falls
 *  back to the default rather than putting an unknown key into a warehouse
 *  query. Everything else in storage is ignored, whatever it claims to be. */
export function mergePersistedUI(persisted: unknown, current: UIState): UIState {
  const range = (persisted as { range?: unknown } | null)?.range;
  return isRangeKey(range) ? { ...current, range } : current;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      ...initial,

      set: (key, value) => set({ [key]: value } as Pick<UIState, typeof key>),

      setLedgerQuery: (key, value) =>
        set({
          [key]: value,
          ledgerPage: 0,
          ledgerCursors: [],
          ledgerSelection: null,
        } as unknown as Partial<UIState>),

      setScope: (scope) =>
        // Leaving a tenant closes anything that was scoped to it, so the operator
        // never lands on a drawer belonging to a tenant they just exited.
        set({ scope, scopePickerOpen: false, ledgerSelection: null, ledgerPage: 0, ledgerCursors: [] }),

      openModal: (modal) => set({ modal, wizardStep: 0 }),
      closeModal: () => set({ modal: null, wizardStep: 0 }),

      selectLedgerRow: (eventId) =>
        set({ ledgerSelection: eventId, drawerTab: 'overview', payloadRevealed: false }),

      clearLedgerFilters: () =>
        set({
          ledgerSearch: '',
          ledgerEstate: 'all',
          ledgerFilter: 'all',
          ledgerPage: 0,
          ledgerCursors: [],
          ledgerSelection: null,
        }),

      clearTenantFilters: () =>
        set({ tenantSearch: '', tenantMode: 'all', tenantPool: 'all' }),

      // The cursor stack is what makes "Previous" possible over a forward-only
      // cursor API: index N holds the cursor that opens page N+1.
      nextLedgerPage: (cursor) =>
        set((s) => ({
          ledgerCursors: [...s.ledgerCursors.slice(0, s.ledgerPage), cursor],
          ledgerPage: s.ledgerPage + 1,
          ledgerSelection: null,
        })),

      prevLedgerPage: () =>
        set((s) => ({
          ledgerPage: Math.max(0, s.ledgerPage - 1),
          ledgerSelection: null,
        })),

      setMemoryFilter: (patch) =>
        set((s) => ({
          memoryFilter: { ...s.memoryFilter, ...patch },
          memoryPage: 0,
          memoryCursors: [],
          memorySelection: null,
        })),
      clearMemoryFilters: () =>
        set({ memoryFilter: defaultMemoryFilter, memoryPage: 0, memoryCursors: [], memorySelection: null }),
      selectMemory: (id) => set({ memorySelection: id }),
      nextMemoryPage: (cursor) =>
        set((s) => ({
          memoryCursors: [...s.memoryCursors.slice(0, s.memoryPage), cursor],
          memoryPage: s.memoryPage + 1,
          memorySelection: null,
        })),
      prevMemoryPage: () =>
        set((s) => ({ memoryPage: Math.max(0, s.memoryPage - 1), memorySelection: null })),
      openMemoryBrowser: () => set({ memoryPage: 0, memoryCursors: [], memorySelection: null }),

      setMemoryEstate: (estate) => set({ memoryEstate: estate, memoryTenantNav: { estate, cursors: [], page: 0 } }),
      nextMemoryTenantPage: (estate, cursor) => set((s) => ({ memoryTenantNav: nextTenantNav(s.memoryTenantNav, estate, cursor) })),
      prevMemoryTenantPage: (estate) => set((s) => ({ memoryTenantNav: prevTenantNav(s.memoryTenantNav, estate) })),

      // Sign-out clears every place and selection, but not the preference: the
      // next operator on this browser is the same operator, and the range is
      // theirs. It is also the one field the data stores' own reset keeps.
      reset: () => set({ ...initial, range: get().range }),
    }),
    {
      name: UI_STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(browserStorage),
      partialize: (s) => ({ range: s.range }),
      // Any stored version reaches merge, which is the one validator: without
      // this, a version bump would discard the preference with a console error
      // before merge ever saw it.
      migrate: (persisted) => persisted as UIState,
      merge: mergePersistedUI,
    },
  ),
);

export const ledgerFilterLabels: Record<LedgerFilter, string> = {
  all: 'All rows',
  billable: 'Billable only',
  attributed: 'Attributed only',
  conflicts: 'Conflicts',
  errors: 'Errors',
};
